import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import ReactDOM from 'react-dom';
import { Bell, X } from 'lucide-react';
import axios from 'axios';

const NotificationBell = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const { tt } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const bellButtonRef = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (userId) {
      fetchMessages();
    }
  }, [userId]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMessages(response.data);
      setUnreadCount(response.data.filter(msg => !msg.read).length);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/my-messages/${messageId}/read`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId ? { ...msg, read: true } : msg
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleBellClick = () => {
    if (!showNotifications && bellButtonRef.current) {
      const rect = bellButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    setShowNotifications(!showNotifications);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        ref={bellButtonRef}
        onClick={handleBellClick}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
        title={tt('Уведомления')}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown Portal - Renders outside component tree */}
      {showNotifications && ReactDOM.createPortal(
        <>
          <div
            className="fixed inset-0"
            style={{zIndex: 99999998, background: 'transparent'}}
            onClick={() => setShowNotifications(false)}
          />
          <div 
            className="fixed w-80 max-w-sm bg-gray-800 border border-gray-600 rounded-lg shadow-xl"
            style={{
              top: dropdownPosition.top + 'px',
              right: dropdownPosition.right + 'px',
              zIndex: 99999999
            }}
          >
            <div className="p-4 border-b border-gray-600">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">{tt('Уведомления')}</h3>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-400">
                  Загрузка...
                </div>
              ) : messages.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                  <p>{tt('Пусто')}</p>
                  <p className="text-sm">{tt('У вас нет новых уведомлений')}</p>
                </div>
              ) : (
                <div className="py-2">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`px-4 py-3 border-b border-gray-700 last:border-b-0 hover:bg-gray-700/50 transition-colors cursor-pointer ${
                        !message.read ? 'bg-blue-900/20' : ''
                      }`}
                      onClick={() => !message.read && markAsRead(message.id)}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="text-sm text-blue-400 font-medium">
                          {message.created_by_name}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-xs text-gray-500">
                            {formatDate(message.created_at)}
                          </div>
                          {!message.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {message.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {messages.length > 0 && (
              <div className="p-3 border-t border-gray-600">
                <button
                  onClick={() => {
                    // Mark all as read
                    const unreadMessages = messages.filter(msg => !msg.read);
                    unreadMessages.forEach(msg => markAsRead(msg.id));
                  }}
                  className="w-full text-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  disabled={unreadCount === 0}
                >
                  {unreadCount > 0 ? 'Отметить все как прочитанные' : 'Все уведомления прочитаны'}
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;